import { createFileRoute, Navigate } from "@tanstack/react-router";
import { z } from "zod";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { useAuth0Login } from "~/hooks/useAuth0Login";
import { useTenant } from "~/hooks/useTenant";
import { Spinner } from "~/components/ui/spinner";
import { Button } from "~/components/ui/button";

export const Route = createFileRoute("/$tenantSlug/")({
  validateSearch: z.object({
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
  component: TenantHomeRedirect,
});

function TenantHomeRedirect() {
  const { tenantSlug } = Route.useParams();
  const { error, error_description } = Route.useSearch();
  const {
    user,
    isLoading: isUserLoading,
    routing,
  } = useAuthenticatedUser();
  const { isAuthenticated, isLoading: isAuthLoading, logout } = useAuth0Login();
  const tenant = useTenant();

  const isLoading = isAuthLoading || isUserLoading;

  if (error) {
    const exampleDomains = tenant.allowedEmailDomains.length
      ? tenant.allowedEmailDomains.map((domain) => `@${domain}`).join(", ")
      : null
    const message =
      error_description === "email_domain_not_permitted"
        ? `Access to this portal requires a university email address${exampleDomains ? ` (allowed: ${exampleDomains})` : ""}.`
        : error_description?.replace(/_/g, " ") || "An error occurred during login.";

    return (
      <div className="min-h-screen bg-white flex flex-col justify-center items-center gap-6 p-8">
        <h1 className="text-2xl font-bold text-black">Access Denied</h1>
        <p className="text-black text-center max-w-md">{message}</p>
        <div className="flex flex-col gap-3 items-center">
          <Button
            onClick={() => logout()}
          >
            Log out
          </Button>
          <a
            href="/signup"
            className="text-sm text-brand-green-primary hover:underline"
          >
            Go to Proudly Human instead
          </a>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="size-8 text-brand-green-primary" />
      </div>
    );
  }

  if (isAuthenticated && Boolean(user)) {
    return <Navigate to={(routing?.homePath ?? `/${tenantSlug}/dashboard`)} />;
  }

  return <Navigate to={`/${tenantSlug}/signup`} />;
}
